<template>
	<!-- LSP-20787 (D18, §6 S5): the invite's delivery inside the Confirmed card, between the heading's hairline and
	     the time rows. It never restyles the heading, the times or the actions — a failed text must not read as a
	     cancelled call. -->
	<div v-if="display" class="d-flex flex-column ga-3 mx-4 mb-3">
		<div
			:class="['sms-delivery-banner', `sms-delivery--${display.tone}`]"
			:data-state="delivery.state"
			:data-tone="display.tone"
			data-test-id="sms-delivery-banner">
			<span v-if="!toneIcon" class="sms-delivery__dot mt-2" aria-hidden="true" />
			<v-icon v-else size="18" class="sms-delivery__glyph" aria-hidden="true">{{ toneIcon }}</v-icon>
			<div class="d-flex flex-column align-start ga-2">
				<span class="sms-delivery-banner__sentence">{{ sentence }}</span>
				<SmsDeliveryActions
					v-if="canRepair && conversation?.id"
					:conversation-id="conversation.id"
					:state="delivery.state"
					:email-invitations-allowed="emailInvitationsAllowed"
					@select="mode = $event"
					@resent="closeAfterSend" />
			</div>
		</div>
		<!-- S5: the inline box opens directly under the banner, still inside the card. :key="mode" remounts the
		     panel fresh on every switch between the phone and email boxes — its contract is to re-seed from the
		     contact each time it opens, and reusing the same instance across a mode switch would leave it holding
		     whatever the rep had typed into the OTHER field. -->
		<SmsDeliveryRepairPanel
			v-if="mode && canRepair && conversation?.id"
			:key="mode"
			:conversation-id="conversation.id"
			:mode="mode"
			:customer="conversation.Customers?.[0] ?? null"
			:participants="conversation.Participants"
			@resent="closeAfterSend"
			@customerUpdated="emit('customerUpdated', $event)"
			@refresh="emit('refresh')"
			@close="mode = null" />
	</div>
</template>

<script setup lang="ts">
	import { computed, ref, watch } from "vue";
	import type { ConversationDelivery } from "@/api/index.d";
	import SmsDeliveryActions from "@/components/SmsDeliveryActions.vue";
	import SmsDeliveryRepairPanel from "@/components/SmsDeliveryRepairPanel.vue";
	import {
		SMS_DELIVERY_DISPLAY,
		SMS_DELIVERY_TONE_ICONS,
		type SmsDeliveryDisplay,
		type SmsDeliveryRepairCustomer,
		type SmsDeliveryRepairParticipant
	} from "@/helpers/smsDelivery";
	import "@/styles/sms-delivery.scss";

	const props = withDefaults(
		defineProps<{
			delivery: ConversationDelivery;
			// AppointmentConfirmation's `conversation`: the page's full entity (id + Customers + Participants).
			conversation: { id: string; Customers?: SmsDeliveryRepairCustomer[]; Participants?: SmsDeliveryRepairParticipant[] } | null;
			callWhen: string;
			canRepair: boolean;
			// Final review I1: the organization's "Allow Email Invitations"; off hides Send via email.
			emailInvitationsAllowed?: boolean;
		}>(),
		{ emailInvitationsAllowed: true }
	);
	const emit = defineEmits<{
		// Bugbot: conversationId is the id the send was actually made for (EntityDetailPage compares it to the
		// session it currently shows before refreshing anything for it).
		(event: "resent", conversationId: string): void;
		(event: "refresh"): void;
		(event: "customerUpdated", customer: SmsDeliveryRepairCustomer): void;
	}>();

	// Which inline box is open. The card is keyed by conversation (EntityDetailPage.vue:245), so a session switch
	// destroys it; a send, Cancel or a new delivery state (the watcher below) closes it.
	const mode = ref<"phone" | "email" | null>(null);
	// B8: a state this table does not know renders no banner (and warns once), never a render error.
	const display = computed<SmsDeliveryDisplay | undefined>(() => SMS_DELIVERY_DISPLAY[props.delivery.state]);
	const toneIcon = computed(() => (display.value ? SMS_DELIVERY_TONE_ICONS[display.value.tone] : null));
	const sentence = computed(() => display.value?.cardSentence.replace("{when}", props.callWhen) ?? "");
	watch(
		() => props.delivery.state,
		(state) => {
			// S7: a new state closes an open box (a generic Resend Invitation's refetch must not leave one under a grey banner).
			mode.value = null;
			if (!SMS_DELIVERY_DISPLAY[state]) console.warn("SmsDeliveryBanner: unknown delivery state, rendering no banner", { state });
		},
		{ immediate: true }
	);

	// After any send from the banner (Resend or the box): close the box; the page refetches the state once at +5 s.
	function closeAfterSend(conversationId: string) {
		mode.value = null;
		emit("resent", conversationId);
	}
</script>

<style scoped lang="scss">
	/* The prototype's banner: tone box, glyph top-left, sentence 15px/600 in the tone text colour, buttons under it. */
	.sms-delivery-banner {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		border-radius: 16px;
		padding: 14px 16px;
		text-align: left;
	}
	.sms-delivery-banner__sentence {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.35;
	}
</style>
